<template>
    <transition
        enter-active-class="transition-all duration-300"
        enter-from-class="opacity-0 translate-y-4"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition-all duration-300"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 translate-y-4"
    >
        <div
            v-if="sessions.length > 0"
            class="fixed bottom-6 right-6 z-[600] w-96 bg-white border border-gray-200 shadow-xl"
            :class="isCollapsed ? 'rounded-xl' : 'rounded-xl'"
        >
            <!-- Header -->
            <div
                class="px-4 py-3 flex items-center justify-between border-b border-gray-200"
                :class="isCollapsed ? 'rounded-xl border-b-0' : 'rounded-t-xl'"
            >
                <div class="flex items-center gap-2">
                    <svg v-if="isUploading" class="w-4 h-4 text-yellow-500 animate-spin shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                    <CheckCircleIcon v-else class="w-4 h-4 text-green-500 shrink-0" />
                    <span class="text-sm font-semibold text-gray-800">{{ headerTitle }}</span>
                </div>
                <div class="flex items-center gap-1">
                    <button
                        @click.prevent="isCollapsed = !isCollapsed"
                        type="button"
                        class="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
                    >
                        <MinusIcon v-if="!isCollapsed" class="w-4 h-4" />
                        <ChevronUpIcon v-else class="w-4 h-4" />
                    </button>
                    <button
                        @click.prevent="dismissAll"
                        type="button"
                        class="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
                    >
                        <XMarkIcon class="w-4 h-4" />
                    </button>
                </div>
            </div>

            <!-- Body -->
            <div v-if="!isCollapsed" class="rounded-b-xl overflow-hidden flex flex-col max-h-80">
                <div v-for="(session, si) in sessions" :key="session.id" class="flex flex-col min-h-0" :class="{ 'border-t border-gray-100': si > 0 }">

                    <!-- Sticky: gallery name + total progress -->
                    <div class="sticky top-0 bg-white z-10 px-4 pt-3 pb-2 border-b border-gray-100">
                        <!-- Gallery name (якщо кілька сесій) -->
                        <p v-if="sessions.length > 1" class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                            {{ session.galleryName }}
                        </p>

                        <!-- Total progress -->
                        <div class="flex flex-col gap-1">
                            <div class="flex justify-between text-xs text-gray-500">
                                <span>{{ $t('upload_progress.total_progress') }}</span>
                                <span>{{ sessionPercent(session) }}%</span>
                            </div>
                            <div class="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden">
                                <div
                                    class="h-full rounded-full transition-all duration-500"
                                    :class="session.isComplete && !hasErrors(session) ? 'bg-green-500' : hasErrors(session) ? 'bg-red-400' : 'bg-yellow-500'"
                                    :style="{ width: `${sessionPercent(session)}%` }"
                                ></div>
                            </div>
                            <div class="flex items-center justify-between mt-0.5">
                                <p class="text-xs text-gray-400">
                                    {{ session.uploadedCount }} {{ $t('upload_progress.of') }} {{ session.totalFiles }} {{ $t('upload_progress.files') }}
                                </p>
                                <!-- Retry all errors button -->
                                <button
                                    v-if="hasErrors(session)"
                                    @click.prevent="retryAllErrors(session)"
                                    type="button"
                                    class="text-xs text-yellow-600 hover:text-yellow-700 font-medium cursor-pointer flex items-center gap-1"
                                >
                                    <ArrowPathIcon class="w-3 h-3" />
                                    {{ $t('upload_progress.retry_all') }}
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Individual files (scrollable) -->
                    <div class="overflow-y-auto px-4 py-3">
                    <div class="space-y-3">
                        <div
                            v-for="file in session.files"
                            :key="file.id"
                            class="flex items-center gap-3"
                            :class="{ 'opacity-50': file.status === 'queued' }"
                        >
                            <!-- Left icon -->
                            <div
                                class="w-8 h-8 rounded flex items-center justify-center shrink-0"
                                :class="{
                                    'bg-gray-100': file.status === 'queued' || file.status === 'uploading',
                                    'bg-green-50': file.status === 'done',
                                    'bg-red-50': file.status === 'error',
                                }"
                            >
                                <CheckIcon v-if="file.status === 'done'" class="w-4 h-4 text-green-600" />
                                <ExclamationCircleIcon v-else-if="file.status === 'error'" class="w-4 h-4 text-red-500" />
                                <PhotoIcon v-else class="w-4 h-4 text-gray-400" />
                            </div>

                            <!-- File info -->
                            <div class="flex-1 min-w-0">
                                <p class="text-sm font-medium text-gray-900 truncate">{{ file.name }}</p>
                                <div v-if="file.status === 'uploading'" class="flex flex-col gap-1 text-xs">
                                    <div class="flex items-center gap-2">
                                        <span class="text-yellow-600 font-medium">{{ $t('upload_progress.uploading_file') }}</span>
                                        <span class="text-gray-400">{{ bytesToSize(file.uploadedSize) }} / {{ bytesToSize(file.size) }}</span>
                                    </div>
                                    <div class="w-full bg-gray-100 h-1 rounded-full overflow-hidden">
                                        <div
                                            class="h-full bg-yellow-500 rounded-full transition-all duration-200"
                                            :style="{ width: `${filePercent(file)}%` }"
                                        ></div>
                                    </div>
                                </div>
                                <p v-else-if="file.status === 'queued'" class="text-xs text-gray-400">{{ $t('upload_progress.queued') }}</p>
                                <p v-else-if="file.status === 'done'" class="text-xs text-green-600 font-medium">{{ $t('upload_progress.file_done') }}</p>
                                <p v-else-if="file.status === 'error'" class="text-xs text-red-500 truncate" :title="file.errorMessage">
                                    {{ file.errorMessage || $t('upload_progress.file_error') }}
                                </p>
                            </div>

                            <!-- Right: spinner / check / retry -->
                            <div class="shrink-0 flex items-center">
                                <div v-if="file.status === 'uploading'" class="w-4 h-4 border-2 border-gray-200 border-t-yellow-500 rounded-full animate-spin"></div>
                                <CheckIcon v-else-if="file.status === 'done'" class="w-4 h-4 text-green-500" />
                                <button
                                    v-else-if="file.status === 'error'"
                                    @click.prevent="retryFile(session, file.id)"
                                    type="button"
                                    class="p-1 rounded text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                                    :title="$t('upload_progress.retry')"
                                >
                                    <ArrowPathIcon class="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                    </div><!-- end overflow-y-auto -->
                </div>
            </div>
        </div>
    </transition>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapGetters } from 'vuex';
import {
    CheckCircleIcon, CheckIcon, ExclamationCircleIcon,
    PhotoIcon, MinusIcon, ChevronUpIcon, XMarkIcon, ArrowPathIcon
} from '@heroicons/vue/20/solid';
import { UploadSession } from '@/store/upload.module';
import { callRetryHandler } from '@/_helpers/uploadRetryRegistry';

interface SessionFile {
    id: string;
    name: string;
    size: number;
    status: 'queued' | 'uploading' | 'done' | 'error';
    errorMessage?: string;
    uploadedSize: number;
}

interface SessionWithFiles extends UploadSession {
    files: SessionFile[];
}

export default defineComponent({
    name: 'GlobalUploadProgressComponent',

    components: {
        CheckCircleIcon, CheckIcon, ExclamationCircleIcon,
        PhotoIcon, MinusIcon, ChevronUpIcon, XMarkIcon, ArrowPathIcon,
    },

    data() {
        return {
            isCollapsed: false,
        };
    },

    computed: {
        ...mapGetters({
            sessions: 'upload/activeSessions',
            isUploading: 'upload/isUploading',
        }),

        headerTitle(): string {
            const totalFiles = (this.sessions as SessionWithFiles[]).reduce((sum: number, s: SessionWithFiles) => sum + s.totalFiles, 0);
            if (this.isUploading) {
                return this.$t('upload_progress.uploading_n_files', { count: totalFiles }) as string;
            }
            return this.$t('upload_progress.done') as string;
        },
    },

    watch: {
        sessions(newSessions: SessionWithFiles[], oldSessions: SessionWithFiles[]) {
            if (newSessions.length > oldSessions.length) {
                // Нова сесія — розгортаємо
                this.isCollapsed = false;
            }
        },
        isUploading(uploading: boolean) {
            if (!uploading) {
                // Всі завантаження завершені — перевіряємо чи немає помилок
                const anyErrors = (this.sessions as SessionWithFiles[]).some(s =>
                    s.files.some((f: SessionFile) => f.status === 'error')
                );
                if (!anyErrors) {
                    // Без помилок — згортаємо через 3 секунди
                    setTimeout(() => {
                        this.isCollapsed = true;
                    }, 3000);
                }
            }
        },
    },

    methods: {
        sessionPercent(session: SessionWithFiles): number {
            if (!session.totalFiles) return 0;
            return Math.round((session.uploadedCount / session.totalFiles) * 100);
        },

        filePercent(file: SessionFile): number {
            if (!file.size) return 0;
            return Math.min(100, Math.round((file.uploadedSize / file.size) * 100));
        },

        hasErrors(session: SessionWithFiles): boolean {
            return session.files.some((f: SessionFile) => f.status === 'error');
        },

        retryFile(session: SessionWithFiles, fileId: string) {
            this.$store.commit('upload/setFileStatus', {
                sessionId: session.id,
                fileId,
                status: 'uploading',
            });
            callRetryHandler(session.id, fileId);
        },

        retryAllErrors(session: SessionWithFiles) {
            const errorFiles = session.files.filter((f: SessionFile) => f.status === 'error');
            this.$store.commit('upload/removeDoneFiles', { id: session.id });
            errorFiles.forEach((f: SessionFile) => this.retryFile(session, f.id));
        },

        dismissAll() {
            (this.sessions as UploadSession[]).forEach(session => {
                this.$store.commit('upload/dismissSession', { id: session.id });
                setTimeout(() => this.$store.commit('upload/removeSession', { id: session.id }), 400);
            });
        },

        bytesToSize(bytes: number): string {
            const sizes = ['B', 'KB', 'MB', 'GB'];
            if (bytes === 0) return '0 B';
            const i = Math.floor(Math.log(bytes) / Math.log(1024));
            return Math.round(bytes / Math.pow(1024, i)) + ' ' + sizes[i];
        },
    },
});
</script>
